import { addDollars, dollarSumBy, multiplyDollars, sumDollars, toCents, toDollars } from "@nuso/common/util-money";
import {
  AddOrUpdateOpportunityQuoteLineItemAndSubProductsInput,
  LineItemSubProduct,
  OpportunityQuote,
  OpportunityQuoteLineItemHardware,
  OpportunityQuoteLineItemPricebookEntry,
  OpportunityQuoteLineItemSubProductInput,
  OpportunityQuoteLineItemTotalsInput,
  PackageratorProductTotals,
  PricebookEntry,
  QuoteLineItem
} from "@nuso/quoting/types";
import { ProductSource } from '@nuso/billing/types';

export const packageratorInitialTotals: PackageratorProductTotals = {
  productTotal: 0,
  hardwareTotal: 0,
  addonsTotal: 0,
  unitPrice: 0,
  listTotal: 0,
  listPartnerPrice: 0,
  salesTotal: 0,
  partnerTotalPrice: 0,
  partnerProductTotal: 0,
  partnerProductPrice: 0,
  partnerHardwarePrice: 0,
  partnerAddonsPrice: 0,
  invalidProducts: [],
};

export const SubProductNames = {
  hardware: 'Hardware',
  addon: 'Add On'
}

// PricebookEntry carries per-term retail/partner price fields named e.g. `rp12`/`pp12`
// dynamically by term length -- there's no static key for these, so look them up via
// an explicit cast rather than an untyped index signature on the whole type.
function pricebookTermPrice(entry: PricebookEntry, field: 'rp' | 'pp', termMonths: number | null): number {
  return (entry as unknown as Record<string, number>)[field + termMonths];
}

export function packageratorCalculateTotals(
  termMonths: number | null,
  pricebookEntries: PricebookEntry[],
  productId: string,
  hardwareId: string,
  addonIds: string[],
  quantity: number,
  partnerUnitPrice: string,
  priceAdjust: string,
): PackageratorProductTotals {
  const totals: PackageratorProductTotals = {...packageratorInitialTotals};

  if (productId) {
    const product = pricebookEntries.find((entry: PricebookEntry) => {
      return entry.id === productId;
    });
    if (!product) {
      throw new Error(`Product '${productId}' not found in pricebook entries`);
    }

    const priceCheck = function(item: PricebookEntry) {
      if (pricebookTermPrice(item, 'rp', termMonths) == null || pricebookTermPrice(item, 'pp', termMonths) == null) {
        totals.invalidProducts.push(item);
      }
    }

    priceCheck(product);
    totals.productTotal = pricebookTermPrice(product, 'rp', termMonths);
    totals.partnerProductPrice = pricebookTermPrice(product, 'pp', termMonths);

    if (hardwareId) {
      const hardware = pricebookEntries.find((entry: PricebookEntry) => {
        return entry.id === hardwareId;
      });
      if (!hardware) {
        throw new Error(`Hardware '${hardwareId}' not found in pricebook entries`);
      }

      priceCheck(hardware);
      totals.hardwareTotal = pricebookTermPrice(hardware, 'rp', termMonths);
      totals.partnerHardwarePrice = pricebookTermPrice(hardware, 'pp', termMonths);
    }

    if (addonIds) {
      const addons: PricebookEntry[] = pricebookEntries.filter((product: PricebookEntry) => {
        return addonIds.includes(product.id);
      });

      addons.forEach((a: PricebookEntry) => priceCheck(a));
      totals.addonsTotal = dollarSumBy(addons, (a: PricebookEntry) => pricebookTermPrice(a, 'rp', termMonths));
      totals.partnerAddonsPrice = dollarSumBy(addons, (a: PricebookEntry) => pricebookTermPrice(a, 'pp', termMonths));
    }
  }
  totals.listTotal = sumDollars([totals.productTotal, totals.hardwareTotal, totals.addonsTotal]);
  totals.listPartnerPrice = sumDollars([
    totals.partnerProductPrice,
    totals.partnerHardwarePrice,
    totals.partnerAddonsPrice,
  ]);
  totals.partnerProductTotal = partnerUnitPrice.length ? Number(partnerUnitPrice) : totals.listPartnerPrice;

  if (quantity) {
    totals.unitPrice = addDollars(totals.listTotal, Number(priceAdjust));
    totals.salesTotal = multiplyDollars(totals.unitPrice, Number(quantity));
    totals.partnerTotalPrice = multiplyDollars(totals.partnerProductTotal, Number(quantity));
  }

  return totals;
}

export function addSubTypesToPackageDescription(
  mainProductName: string,
  hardwareNames: string[] = [],
  addonNames: string[] = []
) {
  const subProductNames = [...hardwareNames, ...addonNames];

  return subProductNames.length > 0
    ? `${mainProductName} w/ ${subProductNames.join(', ')}`
    : mainProductName;
}

// Note, there used to be a addonNames.join(', ').slice(0, -2) which makes no sense to me
// So if we need to restrict the length, we should do it on the end string.
export function calculatePackageDescription(
  mainProductName: string,
  hardware: OpportunityQuoteLineItemHardware | null,
  addons: OpportunityQuoteLineItemSubProductInput[] | null)
{
  const hardwareNames = hardware ? [hardware.name] : [];
  const addonNames = addons ? addons.map(a => a.name) : [];

  return addSubTypesToPackageDescription(mainProductName, hardwareNames, addonNames);
}

export function packageratorLineItemFormUpdates(
  lineItem: QuoteLineItem,
  subProducts: LineItemSubProduct[],
  pricebookEntries: PricebookEntry[],
) {
  const priceAdjust: number = (
    toDollars(
        toCents(lineItem.unitPrice)
      - toCents(lineItem.storedListPrice || lineItem.listPrice) // Stored list price is preferred but fallback to OG listPrice
      - toCents(subProducts.reduce((total, current) => addDollars(total, current.listPrice), 0))
    )
  );

  let hardware: string | undefined;
  const addons: string[] = [];
  subProducts.forEach((subProduct: LineItemSubProduct) => {
    const foundHardware = pricebookEntries.find(
      (el) =>
        el.id === subProduct.pricebookEntryId &&
        el.subProduct === SubProductNames.hardware
    );
    if (foundHardware) {
      hardware = foundHardware.id;
    }

    const foundAddon = pricebookEntries.find(
      (el) =>
        el.id === subProduct.pricebookEntryId &&
        el.subProduct === SubProductNames.addon
    );
    if (foundAddon) {
      addons.push(foundAddon.id);
    }
  });

  const packageratorFormUpdates = {
    serviceLocation: lineItem.locationId,
    service: lineItem.serviceType,
    category: lineItem.category,
    product: lineItem.pricebookEntryId,
    quantity: Number(lineItem.quantity),
    partnerUnitPrice: lineItem.partnerUnitPrice.toFixed(2),
    priceAdjust: priceAdjust.toFixed(2),
    hardware,
    addons,
  };

  return packageratorFormUpdates;
}

export function calculateOpportunityQuoteLineItemInput(
  termMonths: number | null,
  packageratorTotals: PackageratorProductTotals,
  quote: OpportunityQuote,
  pricebookEntries: PricebookEntry[],
  formValue: {
    serviceLocation?: string | null;
    service?: string | null;
    category?: string | null;
    product?: string | null;
    hardware?: string | null;
    addons?: string[];
    quantity?: number;
    priceAdjust?: string;
  },
  currentLineItemId: string | undefined,
  productSource: ProductSource = 'revio',
) {
  // Some short-circuiting
  const { serviceLocation, service, category, product, quantity } = formValue;
  if (productSource === 'revio') {
    if (!serviceLocation || !service || !category || !product || !quantity || !quote) {
      throw `Missing one of: serviceLocation (${serviceLocation}), service (${service}),`
        + ` category (${category}), product (${product}), quantity (${quantity}), or the quote itself.`;
    }
  } else {
    if (!category || !product || !quantity || !quote) {
      throw `Missing one of: category (${category}), product (${product}), quantity (${quantity}), or the quote itself.`;
    }
  }

  const totals: OpportunityQuoteLineItemTotalsInput = {
    listTotal: packageratorTotals.listTotal,
    productTotal: packageratorTotals.productTotal,
    hardwareTotal: packageratorTotals.hardwareTotal,
    addonsTotal: packageratorTotals.addonsTotal,
    unitPrice: packageratorTotals.unitPrice,
    salesTotal: packageratorTotals.salesTotal,
    partnerProductTotal: packageratorTotals.partnerProductTotal,
    partnerTotalPrice: packageratorTotals.partnerTotalPrice,
  }

  // This variant will eliminate missing entries from the pricebook in the final payload, instead of having empty objects.
  const addons: OpportunityQuoteLineItemSubProductInput[] = pricebookEntries
    .filter((entry: PricebookEntry) => (formValue.addons || []).includes(entry.id))
    .map((entry: PricebookEntry) => ({
      id: entry.id,
      name: entry.name,
      retailPrice: pricebookTermPrice(entry, 'rp', termMonths),
      partnerPrice: pricebookTermPrice(entry, 'pp', termMonths),
      productId: entry.productId,
    }));

  const pricebookEntry: OpportunityQuoteLineItemPricebookEntry | undefined = pricebookEntries
    .filter((entry: PricebookEntry) => entry.id === product)
    .map((entry: PricebookEntry) => ({
      id: entry.id,
      name: entry.name,
      productId: entry.productId,
    }))
    .shift(); // should only be one, or undefined if no match

  const hardware: OpportunityQuoteLineItemHardware | null = pricebookEntries
    .filter((entry: PricebookEntry) => entry.id === formValue.hardware)
    .map((entry: PricebookEntry) => ({
      id: entry.id,
      name: entry.name,
      retailPrice: pricebookTermPrice(entry, 'rp', termMonths),
      partnerPrice: pricebookTermPrice(entry, 'pp', termMonths),
      productId: entry.productId,
    }))
    .shift() || null; // If no match is found, use null instead of undefined.

  // After some calculations, some more safety checks.
  if (!pricebookEntry) {
    throw `Product '${formValue.product}' could not be found in the pricebook, cannot add line item.`;
  }

  const quoteLineItem: AddOrUpdateOpportunityQuoteLineItemAndSubProductsInput = {
    // Only validated above for productSource === 'revio'; for other sources these are
    // expected to already be populated by the caller (e.g. an existing quote's line item).
    // Not provable for non-revio sources; a fallback/throw here would change behavior for a
    // path this batch can't confirm is unreachable.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    serviceLocation: serviceLocation!,
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- see comment above
    service: service!,
    category,
    product,
    hardware,
    priceAdjust: Number(formValue.priceAdjust),
    quantity: Number(quantity),
    addons,
    totals,
    pricebookEntry,
    customerId: quote.customer.id,
    opportunityId: quote.opportunityId,
    quoteId: quote.id,
    pricebookId: quote.pricebook.id,
    lineItemId: currentLineItemId,
  }

  return quoteLineItem;
}
